import { useMsal } from '@azure/msal-react';
import { ICommandBarItemProps } from '@fluentui/react';
import { RootState } from 'app/store';
import saveAs from 'file-saver';
import { emitCustomEvent } from 'global/Application';
import { useApplication } from 'global/Application/Context';
import { LoadConfigAsync, SaveAction } from 'global/Application/dataActions';
import { useAuthentication } from 'global/Authentication';
import { AppDataSource } from 'global/DataSources/AppDataSource';
import { MsalTokenSource } from 'global/DataSources/MsalTokenSource';
import { addUpdateORE, IGCDataLight, setPharmacyORE } from 'global/Reducer/dataSlice';
import { setProcessing } from 'global/Reducer/statusSlice';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import CompareLookupDialog from 'views/BlueRelationship/CompareLookupDialog';
import { ECompareLookupDialogVariant, ICompareConfiguration, IDialogResult } from 'views/BlueRelationship/CompareLookupDialog/CompareLookupDialog';
import GroupConfigurationSnapshotContext from 'views/BlueRelationship/GroupConfigurationSnapshotContext';
import { SyncPharmacyExtractList } from '../PrimeHelpers';
import PharmacyList from './PharmacyList';


export interface IPharmacyContextProps {
    isFloridaPrime?: boolean;
    isCapitalPrime?: boolean;
}


enum EPrimeIntent {
    Extract,
}

let compareLookupDialogOpenAcrossRemounts = true;

// rename to context
const PharmacyContext: React.FC<IPharmacyContextProps> = (props) => {

    const authentication = useAuthentication();
    const application = useApplication();
    const state = useSelector((state: RootState) => state);

    const [isOpenCompareLookupDialog, setIsOpenCompareLookupDialog] = useState<boolean>(
        () => compareLookupDialogOpenAcrossRemounts
    );

    const dispatch = useDispatch();

    const { instance } = useMsal();
    const tokenSource = new MsalTokenSource(instance);
    const dataSource = new AppDataSource(tokenSource);

    const history = useHistory();

    const resyncPharmacyExtractList = (reload: boolean) => {

        if (state.data.json) {

            const noPharmacyORE = (state.data.json?.pharmacyORE?.length ?? 0) === 0;

            if (reload || noPharmacyORE) {
                const list = SyncPharmacyExtractList(
                    state.data.json,
                    state.data.config,
                    props,
                );

                if (list.length > 0)
                    dispatch(addUpdateORE(list));
            }
        }
    };

    useEffect(() => {

        resyncPharmacyExtractList(false);

    }, [state.data.json, state.data.config]);

    useEffect(() => {
        // on mount, tell menu what to display
        document.title = `Pharmacy Extract | ${state.data?.snapshot?.bcbs_name ?? '-'}`;

    }, [state?.data?.snapshot])

    const contextMenu = useMemo(() => {
        const menu: ICommandBarItemProps[] = [];

        const canRelease = (state.data.modifiedOn ?? -1) > (state.data.savedOn ?? 0)

        menu.push({
            key: 'save',
            iconProps: { iconName: 'Save' },
            text: 'Save',

            onClick: () => {
                dispatch(SaveAction(
                    dataSource,
                    history,
                    {
                        ignoreViewType: true,
                    }
                ));
            }
        });

        if (authentication.authorizations?.roles['BCBS - Power User'] || authentication.authorizations?.roles['BCBS - Pharmacy'])
            menu.push({
                key: 'pharmacy',
                text: 'Release',
                title: 'Release Pharmacy Extract To BCBS',
                disabled: canRelease,
                iconProps: { iconName: 'Send' },
                onClick: () => {
                    onExtractAsync(true);
                }
            });

        menu.push({
            key: 'generate',
            iconProps: { iconName: 'Generate' },
            text: 'Generate',
            title: 'Generate Pharmacy Extract Without Releasing To BCBS',
            disabled: canRelease,
            onClick: () => {
                onExtractAsync(false);
            }
        });

        menu.push({
            key: 'compare',
            iconProps: { iconName: 'Generate' },
            text: 'Compare',
            // disabled: canRelease,
            onClick: () => {
                compareLookupDialogOpenAcrossRemounts = true;
                setIsOpenCompareLookupDialog(true);
            }
        });

        const isAdmin = state.status?.debug?.admin ?? false;

        menu.push({
            key: 'refresh',
            iconProps: { iconName: 'Refresh' },
            text: 'Refresh',
            split: isAdmin,
            subMenuProps: isAdmin ? {
                items: [
                    {
                        key: 'reset',
                        text: 'Reset Pharmacy Extract',
                        onClick: () => {
                            dispatch(setPharmacyORE([]));
                        }
                    }
                ]
            } : undefined,
            onClick: () => {
                resyncPharmacyExtractList(true);
            }
        });

        return {
            items: menu,
            key: 'menu.pharmacy'
        };

    }, [
        state.data,
        state.status?.debug?.admin,
        authentication.authorizations?.roles
    ]);

    const onExtractAsync = async function (release: boolean) {

        try {
            const bcbs_groupconfigurationsnapshotid = state.data.snapshot.bcbs_groupconfigurationsnapshotid!;

            dispatch(setProcessing(true))

            const response = await dataSource.getAsyncHttpStartGeneratePharmacyExtract(
                {
                    bcbs_groupconfigurationsnapshotid,
                    release: release,
                    isFloridaPrime: props.isFloridaPrime ?? false,
                    isCapitalPrime: props.isCapitalPrime ?? false,
                }
            );

            if (!release)
                saveAs(response.content,
                    response.name,
                    {
                        autoBom: true
                    }
                );

        } catch (err: any) {
            emitCustomEvent({
                eventType: 'globalException',
                details: err
            });
            console.error(err);
        } finally {
            dispatch(setProcessing(false))
        }
    };


    const compareLookupDialogOnContinueAsync = async (viewModel: ICompareConfiguration) => {

        const result: IDialogResult = {
            dismiss: true,
        };

        try {

            // right snapshot is always the current snapshot, left snapshot is the one selected in the dialog
            if (viewModel.leftSnapshotRef && viewModel.leftSnapshotRef) {

                const origin_bcbs_groupconfigurationsnapshot = await application.dataSource.getGroupConfigurationSnapshot(viewModel.leftSnapshotRef.id!);

                // load the config release if it's not the same as the current snapshot
                const bcbs_config_release = origin_bcbs_groupconfigurationsnapshot._bcbs_releaseid_value != state.data.snapshot._bcbs_releaseid_value
                    ? await application.dataSource.getRelease(origin_bcbs_groupconfigurationsnapshot._bcbs_releaseid_value!)
                    : state.data.config.bcbs_config_release

                // load the config json if it's not the same as the current snapshot
                const config = origin_bcbs_groupconfigurationsnapshot._bcbs_releaseid_value != state.data.snapshot._bcbs_releaseid_value
                    ? await LoadConfigAsync(
                        application.dataSource,
                        bcbs_config_release,
                    )
                    : state.data.config;

                const account = state.data.account!;

                // load the snapshot json if it's not the same as the current snapshot
                let downloadSnapshotResult = await application.dataSource.downloadGroupConfigurationSnapshot(origin_bcbs_groupconfigurationsnapshot);

                if (!downloadSnapshotResult)
                    throw new Error('Snapshot not found');

                const originState: IGCDataLight = {
                    json: downloadSnapshotResult.json,
                    snapshot: origin_bcbs_groupconfigurationsnapshot,
                    config: config,
                    account: account,
                };

                // TODO structure clone these and remove the processing structure clone
                const targetState: IGCDataLight = {
                    json: state.data.json,
                    snapshot: state.data.snapshot,
                    config: state.data.config,
                    account: account,
                };

                const [_, setComparisonState] = application.comparisonState

                setComparisonState({
                    origin: originState,
                    target: targetState,
                    configuration: structuredClone(viewModel),
                });
            }

        } catch (err: any) {
            emitCustomEvent({
                eventType: 'globalException',
                details: err
            });
        }
        finally {
            dispatch(setProcessing(false))
            return result;
        }
    }

    const compareLookupDialogRender = () => {
        return (
            <CompareLookupDialog
                variant={ECompareLookupDialogVariant.PharmacyDialog}
                onDismiss={() => {
                    compareLookupDialogOpenAcrossRemounts = false;
                    setIsOpenCompareLookupDialog(false);
                }}
                onContinue={compareLookupDialogOnContinueAsync}
                isOpen={isOpenCompareLookupDialog}
            />
        );
    };


    return (
        <GroupConfigurationSnapshotContext
            contextMenu={contextMenu}
            hideSave
        >
            {compareLookupDialogRender()}

            <PharmacyList
                contextProps={props}
            />

        </GroupConfigurationSnapshotContext>
    );
};

export default PharmacyContext;